import type { ScopeQuery } from "@app/shared"
import { z } from "zod"
import { http } from "../lib/http"
import { type PartyRole, PartyRoleSchema } from "./parties"

/**
 * Merchant-change detection layer enum -- mirrors PRD §7's 5-layer
 * detection model:
 *   - L1_MANUAL          -- operator-flagged
 *   - L2_DBL_CLASSIFIER  -- diff against the canonical document
 *   - L3_SLOT_WATCH      -- structural Party.id swap
 *   - L4_API_EVENT       -- carrier-API-reported event
 *   - L5_PATTERN_SIGNAL  -- ML / heuristic signal
 */
export const MerchantChangeDetectionLayerSchema = z.enum([
	"L1_MANUAL",
	"L2_DBL_CLASSIFIER",
	"L3_SLOT_WATCH",
	"L4_API_EVENT",
	"L5_PATTERN_SIGNAL",
])

/**
 * Merchant-change classification enum. Initial state is
 * `PENDING_REVIEW`; operator narrows it to one of HEIST /
 * TREATED_AS_CORRECTION / DISMISSED.
 */
export const MerchantChangeClassificationSchema = z.enum([
	"PENDING_REVIEW",
	"HEIST",
	"TREATED_AS_CORRECTION",
	"DISMISSED",
])

export type MerchantChangeDetectionLayer = z.infer<typeof MerchantChangeDetectionLayerSchema>
export type MerchantChangeClassification = z.infer<typeof MerchantChangeClassificationSchema>

export const MerchantChangeSchema = z.object({
	// Resolved display labels. The ids stay on the object because the page
	// links by id, but nothing should RENDER one — see `design_system.md`.
	shipmentRef: z.string(),
	previousPartyName: z.string().nullable(),
	newPartyName: z.string().nullable(),
	detectedByLabel: z.string().nullable(),
	classifiedByLabel: z.string().nullable(),
	id: z.string(),
	shipmentId: z.string(),
	documentId: z.string().nullable(),
	partyRole: PartyRoleSchema,
	previousPartyId: z.string(),
	newPartyId: z.string(),
	detectionLayer: MerchantChangeDetectionLayerSchema,
	detectedAt: z.string(),
	detectedBy: z.string().nullable(),
	classification: MerchantChangeClassificationSchema,
	classifiedBy: z.string().nullable(),
	classifiedAt: z.string().nullable(),
	notes: z.string().nullable(),
	createdAt: z.string(),
	updatedAt: z.string(),
})

const MerchantChangeListSchema = z.array(MerchantChangeSchema)

export type MerchantChange = z.infer<typeof MerchantChangeSchema>

/** GET /shipments/:shipmentId/heists */
export const listShipmentHeists = async (
	shipmentId: string,
	signal?: AbortSignal,
): Promise<MerchantChange[]> => {
	const raw = await http<unknown>(
		`/shipments/${encodeURIComponent(shipmentId)}/heists`,
		signal !== undefined ? { signal } : {},
	)
	return MerchantChangeListSchema.parse(raw)
}

/** GET /heists/:id -- 404 surfaces as HttpError(status=404). */
export const getHeist = async (id: string, signal?: AbortSignal): Promise<MerchantChange> => {
	const raw = await http<unknown>(
		`/heists/${encodeURIComponent(id)}`,
		signal !== undefined ? { signal } : {},
	)
	return MerchantChangeSchema.parse(raw)
}

/**
 * Body of `PATCH /heists/:id/classification`. `classification` is one
 * of the three terminal classifications; PENDING_REVIEW is the
 * initial state and is rejected by the engine.
 */
export interface ClassifyHeistInput {
	readonly classification: Exclude<MerchantChangeClassification, "PENDING_REVIEW">
	readonly notes?: string
}

export const classifyHeist = async (
	id: string,
	input: ClassifyHeistInput,
	signal?: AbortSignal,
): Promise<MerchantChange> => {
	const raw = await http<unknown>(`/heists/${encodeURIComponent(id)}/classification`, {
		method: "PATCH",
		json: input,
		...(signal !== undefined ? { signal } : {}),
	})
	return MerchantChangeSchema.parse(raw)
}

/**
 * Tenant-wide list row. The engine resolves every foreign key to a name
 * before it reaches here — see `design_system.md`: never surface a raw FK
 * UUID. `shipmentRef` is booking -> BL -> "Draft", never the shipment UUID.
 */
export const MerchantChangeListItemSchema = z.object({
	id: z.string(),
	shipmentId: z.string(),
	shipmentRef: z.string(),
	partyRole: PartyRoleSchema,
	previousPartyName: z.string().nullable(),
	newPartyName: z.string().nullable(),
	detectionLayer: MerchantChangeDetectionLayerSchema,
	detectedAt: z.string(),
	detectedByLabel: z.string().nullable(),
	classification: MerchantChangeClassificationSchema,
	classifiedByLabel: z.string().nullable(),
	classifiedAt: z.string().nullable(),
})

export type MerchantChangeListItem = z.infer<typeof MerchantChangeListItemSchema>

const MerchantChangeListItemsSchema = z.array(MerchantChangeListItemSchema)

/**
 * `GET /heists` — the screen the dashboard's "Heists pending review" card
 * links to. Scope params are forwarded the same way `/lots` forwards them,
 * because the dashboard count scopes through `shipments` identically; any
 * divergence makes the card and this list disagree.
 */
export const listHeists = async (
	options: {
		scope?: ScopeQuery
		classification?: MerchantChangeClassification
		signal?: AbortSignal
	} = {},
): Promise<MerchantChangeListItem[]> => {
	const search = new URLSearchParams()
	if (options.scope?.country) search.set("country", options.scope.country)
	if (options.scope?.vertical) search.set("vertical", options.scope.vertical)
	if (options.classification) search.set("classification", options.classification)
	const qs = search.toString()
	const raw = await http<unknown>(`/heists${qs.length > 0 ? `?${qs}` : ""}`, {
		...(options.signal !== undefined ? { signal: options.signal } : {}),
	})
	return MerchantChangeListItemsSchema.parse(raw)
}

export const heistsQueryKeys = {
	all: ["heists"] as const,
	// Scope + classification segments are appended only when set, so `list()`
	// stays a valid invalidation prefix for every filtered variant.
	list: (scope?: ScopeQuery, classification?: MerchantChangeClassification): readonly string[] => {
		const key: string[] = [...heistsQueryKeys.all, "list", "tenant"]
		if (scope?.country) key.push(scope.country)
		if (scope?.vertical) key.push(scope.vertical)
		if (classification) key.push(classification)
		return key
	},
	listByShipment: (shipmentId: string) =>
		[...heistsQueryKeys.all, "list", "shipment", shipmentId] as const,
	detail: (id: string) => [...heistsQueryKeys.all, "detail", id] as const,
}

/**
 * Body of `POST /heists` — PRD §7's L1 manual record.
 *
 * `detectionLayer` and `classification` are deliberately absent: the engine
 * stamps L1_MANUAL and PENDING_REVIEW. A client cannot claim its hand-entered
 * record came from a carrier API event, nor record and self-classify in one
 * call — recording and judging stay two acts.
 */
export interface RecordHeistInput {
	readonly shipmentId: string
	readonly partyRole: PartyRole
	readonly previousPartyId: string
	readonly newPartyId: string
	readonly notes?: string
}

export const recordHeist = async (
	input: RecordHeistInput,
	signal?: AbortSignal,
): Promise<MerchantChange> => {
	const raw = await http<unknown>("/heists", {
		method: "POST",
		json: input,
		...(signal !== undefined ? { signal } : {}),
	})
	return MerchantChangeSchema.parse(raw)
}
